import React from 'react';
import { FormattedMessage } from 'react-intl';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCheckCircle, faTimesCircle } from '@fortawesome/free-solid-svg-icons';
import { LAWFUL_PRESENCE_VLP_STATUSES, VERIFICATION } from '../constants';
import { useExchangeAndProgramNames } from '@reducers/appReducer';

export interface ILawfulPresenceVlpStatuses {
    medicaidVlpStatus?: string | null;
    aptcVlpStatus?: string | null;
    qhpVlpStatus?: string | null;
}

interface ILawfulPresenceVlpStatusTagsProps {
    applicantDisplayName: string;
    verificationType: string;
    statuses: ILawfulPresenceVlpStatuses;
}

/**
 * Renders the program-specific Lawful Presence Verification status
 * tags (Medicaid/CHIP, APTC/CSR, QHP) on the DMI Verifications screen when
 * `viewElectricVerification` is true and at least one status is non-null.
 */
const LawfulPresenceVlpStatusTags = ({
    applicantDisplayName,
    verificationType,
    statuses,
}: ILawfulPresenceVlpStatusTagsProps) => {
    const exchangeAndProgramNames = useExchangeAndProgramNames();
    const visibleStatuses = LAWFUL_PRESENCE_VLP_STATUSES.filter(
        ({ statusKey }) => statuses?.[statusKey] != null,
    );

    if (visibleStatuses.length === 0) {
        return null;
    }
    return (
        <div
            className="pt-3 px-3 pb-1"
            data-testid={`dmi-verifications-${applicantDisplayName}-${verificationType}-vlp-statuses`}
        >
            {visibleStatuses.map(({ statusKey, labelId }) => {
                const isVerified = statuses[statusKey] === VERIFICATION.VERIFIED;
                return (
                    <div
                        key={statusKey}
                        className="d-flex flex-wrap align-items-center gap-3 mb-4"
                        data-testid={`dmi-verifications-${applicantDisplayName}-${statusKey}`}
                    >
                        <p className="m-0" style={{ fontWeight: 600 }}>
                            <FormattedMessage
                                id={labelId}
                                values={{
                                    exchangeMedicaidOrChip: exchangeAndProgramNames.exchangeMedicaidOrChip,
                                    exchangeMedicaidSlashChip: exchangeAndProgramNames.exchangeMedicaidSlashChip,
                                    exchangeMedicaid: exchangeAndProgramNames.exchangeMedicaid,
                                }}
                            />
                            {':'}
                        </p>
                        <span
                            className={`app-status-indicator badge py-2 px-3 ${
                                isVerified ? 'qle-document-info__item--VERIFIED' : 'qle-document-info__item--REJECTED'
                            }`}
                            style={{ fontWeight: 600 }}
                            data-testid={`dmi-verifications-${applicantDisplayName}-${statusKey}-tag`}
                        >
                            <FontAwesomeIcon icon={isVerified ? faCheckCircle : faTimesCircle} className="mr-2" />
                            <FormattedMessage
                                id={isVerified ? 'common.status.verified' : 'common.status.not_verified'}
                            />
                        </span>
                    </div>
                );
            })}
        </div>
    );
};

export default LawfulPresenceVlpStatusTags;
